import { useCallback, useState, useEffect, forwardRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import axios from "axios";
import { useDepartmentsQueryDisableable } from "../../../Hooks/useDepartmentsQuery";

import ListSelect from "../../../components/Utils/ListSelect";
import { Checkbox, Typography } from "@mui/material";
import {
	updateExamReqClassID,
	getClasslistLDap,
} from "../../../components/Api/ExamGradingEndpoints";

import clsx from "clsx";
import LinearProgress from "@mui/material/LinearProgress";
import {
	DataGrid,
	Toolbar,
	ToolbarButton,
	QuickFilter,
	QuickFilterControl,
	QuickFilterClear,
	QuickFilterTrigger,
} from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/Cancel";
import CircularProgress from "@mui/material/CircularProgress";
import { useSetAtom, useAtomValue } from "jotai";
import {
	currentOmrRequestAtom,
	classListFileAtom,
	termsAtom,
} from "../../../components/Atoms/atoms";

function compareClassTitles(leftTitle, rightTitle) {
	const parseTitle = (title) => {
		const normalizedTitle = String(title || "").trim();
		const parts = normalizedTitle.split(" ");

		if (parts.length === 5) {
			return [parts[0] + " " + parts[1], parts[2], parts[3], parts[4]];
		}

		return parts;
	};

	const leftParts = parseTitle(leftTitle);
	const rightParts = parseTitle(rightTitle);
	const maxLength = Math.max(leftParts.length, rightParts.length);

	for (let index = 0; index < maxLength; index += 1) {
		const leftPart = leftParts[index] ?? "";
		const rightPart = rightParts[index] ?? "";

		if (leftPart < rightPart) {
			return -1;
		}

		if (leftPart > rightPart) {
			return 1;
		}
	}

	return 0;
}

export default function ClassListCheckDialog({
	open,
	onClose,
	mode = "invalid",
	token,
	fetchRequestDetails,
	setLoadingClassList,
}) {
	const currentOmrRequest = useAtomValue(currentOmrRequestAtom);
	const setClassListFile = useSetAtom(classListFileAtom);
	const terms = useAtomValue(termsAtom);
	const [classNumList, setClassNumList] = useState([]);
	const [deptId, setDeptId] = useState(null);
	const [departmentsList, setDepartmentsList] = useState([]);
	const [selectedDept, setSelectedDept] = useState(null);

	const { data: departments } = useDepartmentsQueryDisableable(token, open);

	const [selectedClassIds, setSelectedClassIds] = useState([]);

	const [loadingUpdateClassID, setLoadingUpdateClassID] = useState(false);

	// Reset selection when the department changes (optional but nice)
	useEffect(() => {
		setSelectedClassIds([]);
	}, [deptId]);

	useEffect(() => {
		if (
			currentOmrRequest.dept &&
			currentOmrRequest.dept.length > 0 &&
			departments &&
			departments.length > 0
		) {
			// Split the string and convert each ID to a number
			const deptArray = currentOmrRequest.dept.split(",").map((deptId) => Number(deptId.trim()));

			// Take the first department ID from the array
			const firstDeptId = deptArray[0];

			// Find the matching department
			const dept = departments.find((dept) => dept.key === firstDeptId);

			if (dept) {
				setSelectedDept(dept.department);
				setDeptId(firstDeptId);
			}
		}
	}, [currentOmrRequest, departments]);

	useEffect(() => {
		if (departments && departments.length > 0) {
			//map object to list of department names
			const deptNames = departments.map((dept) => dept.department);
			setDepartmentsList(deptNames);
		}
	}, [departments]);

	useEffect(() => {
		if (deptId) {
			getClassesFromDepartments(token, deptId);
		}
	}, [deptId, token]);

	const getClassesFromDepartments = (token, deptID) => {
		axios({
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: `Token ${token}`,
			},
			method: "get",
			url:
				process.env.REACT_APP_BACKEND_HOST +
				"/scanner/department_classes_list/" +
				String(deptID) +
				"/",
		}).then((res) => {
			let listClassNum = [];
			if (Array.isArray(res.data) && res.data.length > 0) {
				listClassNum = res.data.map((c) => ({
					key: c.CLASSID,
					value: c.CLASSID,
					label: c.CTITLE,
					name: c.NAME,
					term: c.TERM_NAME,
					term_value: c.TERM,
					term_start: c.TERM_START,
					term_end: c.TERM_END,
					ccid: c.CCID,
					csize: c.CSIZE,
				}));

				const sorted = [...listClassNum].sort((leftClass, rightClass) => {
					const leftTermValue = Number(leftClass.term_value) || 0;
					const rightTermValue = Number(rightClass.term_value) || 0;

					if (leftTermValue !== rightTermValue) {
						return leftTermValue - rightTermValue;
					}

					return compareClassTitles(leftClass.label, rightClass.label);
				});

				setClassNumList(sorted);
			} else {
				setClassNumList([]);
			}
		});
	};

	const isManualMode = mode === "manual";
	const handleClose = useCallback(() => {
		setSelectedClassIds([]);
		onClose?.();
	}, [onClose]);

	const handleDepartmentChange = (event) => {
		const deptName = event.target.value;
		setSelectedDept(deptName);

		const dept = departments.find((dept) => dept.department === deptName);
		setDeptId(dept.key);
		//queryClient.invalidateQueries(["useBlockIdsByDeptQuery", token]);
	};
	const TextInput = forwardRef(function TextInput(props, ref) {
		return (
			<input
				ref={ref}
				{...props}
				className={clsx(
					"h-9 w-full rounded border border-neutral-200  px-2.5 text-base text-neutral-900  focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary",
					props.className,
				)}
			/>
		);
	});
	const renderTerm = (params) => {
		const termColorMap = {
			Fall: "text-white bg-primary",
			Spring: "text-black bg-secondaryLime",
			Winter: "text-secondaryIndigo bg-gray-300 font-semibold",
			Summer: "text- bg-primaryGold",
		};
		return (
			<div className="flex flex-col justify-center h-full">
				<Typography
					variant="body1"
					className={`rounded-md px-1 w-28 text-center ${
						termColorMap[params.row.term.split(" ")[0]]
					}`}
				>
					{params.row.term}
				</Typography>
			</div>
		);
	};

	const renderSelectCourse = (params) => {
		const id = params.row.key; // your getRowId uses row.key
		const checked = selectedClassIds.includes(id);

		return <Checkbox checked={checked} value={id} />;
	};

	const columns = [
		{
			field: "key",
			headerName: "CLASS ID",
			width: 100,
		},
		{
			field: "label",
			headerName: "CLASS TITLE",
			width: 200,
			flex: 0.3,
		},
		{
			field: "csize",
			headerName: "# STUDENTS",
			width: 50,
			flex: 0.1,
		},
		{
			field: "term",
			headerName: "TERM",
			width: 90,
			renderCell: renderTerm,
			flex: 0.2,
		},
		{
			field: "name",
			headerName: "INSTRUCTOR(s)",
			width: 90,
			flex: 0.3,
		},
		{
			field: "select",
			headerName: "SELECT",
			width: 90,
			flex: 0.1,
			renderCell: renderSelectCourse,
		},
	];

	const CustomToolbar = () => {
		return (
			<Toolbar className="gap-2! p-2!">
				<QuickFilter
					render={(props, state) => (
						<div {...props} className="mr-auto flex overflow-clip">
							<QuickFilterTrigger
								className={state.expanded ? "rounded-r-none border-r-0" : ""}
								render={
									<ToolbarButton
										render={
											<Button aria-label="Search">
												<SearchIcon fontSize="small" />
											</Button>
										}
									/>
								}
							/>
							<div
								className={clsx(
									"flex overflow-clip transition-all duration-300 ease-in-out",
									state.expanded ? "w-48" : "w-0",
								)}
							>
								<QuickFilterControl
									aria-label="Search"
									placeholder="Search"
									render={({ slotProps, size, ...controlProps }) => (
										<TextInput
											{...controlProps}
											{...slotProps?.htmlInput}
											className={clsx(
												"flex-1 rounded-l-none",
												state.expanded && state.value !== "" && "rounded-r-none",
											)}
										/>
									)}
								/>
								{state.expanded && state.value !== "" && (
									<QuickFilterClear
										render={
											<Button aria-label="Clear" className="rounded-l-none">
												<CancelIcon fontSize="small" />
											</Button>
										}
									/>
								)}
							</div>
						</div>
					)}
				/>
			</Toolbar>
		);
	};

	const handleClickClasslistRedo = async (classid) => {
		setLoadingClassList(true);
		try {
			// default to the current req’s class id if not provided
			const cid = classid ? String(classid) : currentOmrRequest.class_ids;

			// Resolve the numeric term code for the current request.
			let termCode = 1940; // fallback
			if (currentOmrRequest?.term) {
				termCode = currentOmrRequest.term; // use term from request if available
			} else if (terms?.termNumber) {
				termCode = terms.termNumber; // use your model’s numeric code 1940; //
			}

			const response = await getClasslistLDap(token, cid, String(termCode));
			setClassListFile({
				classlist: response.ldap_data || [],
				class_ids: cid,
			});
			return response;
		} catch (err) {
			alert("Error fetching class list. Please contact TSQS for assistance.", err);
		} finally {
			setLoadingClassList(false);
		}
	};

	const handleUpdateReqClassID = async () => {
		if (!selectedClassIds || selectedClassIds.length === 0) return;
		setLoadingUpdateClassID(true);
		const joined = selectedClassIds.join(",");
		try {
			// 1) Update the request on the backend
			await updateExamReqClassID(token, currentOmrRequest.reqnum, joined);

			// 3) Pull a fresh class list for the selected classes
			await handleClickClasslistRedo(joined);

			// 4) Refresh full request details if needed
			await fetchRequestDetails();
			//TODO: CHECK THIS await handleRescoreExam();
		} catch (error) {
			console.error("Failed to update currentOmrRequest class ids:", error);
		} finally {
			setLoadingUpdateClassID(false);
			handleClose();
		}
	};

	return (
		<Dialog
			open={open}
			onClose={(event, reason) => {
				void event;
				if (!isManualMode && (reason === "backdropClick" || reason === "escapeKeyDown")) {
					return;
				}
				// Reset any selected class IDs when closing the dialog

				handleClose();
			}}
			fullScreen
			maxWidth={false}
			disableEscapeKeyDown={!isManualMode}
			className="bg-digitalWhite"
		>
			<div className="flex flex-col items-center w-full h-full">
				<DialogTitle className="w-full flex justify-between items-center">
					<div>
						{isManualMode ? (
							<>
								Select Class for Grading Request (
								<strong className="text-primary">
									{currentOmrRequest?.class_ids || "No Class ID"}
								</strong>
								)
							</>
						) : (
							<>
								Problem with Class List (
								<strong className="text-red-700">{currentOmrRequest?.class_ids}</strong>)
							</>
						)}
					</div>
				</DialogTitle>
				<DialogContent className="w-full ">
					<div className="flex flex-col gap-4 w-full ">
						{isManualMode ? (
							<>
								<p>
									Select a class below to replace the current class IDs on this grading request.
								</p>
								<p>
									Use this when you want to manually switch to a different class before matching
									students or downloading results.
								</p>
							</>
						) : (
							<>
								<p>
									The CLASS ID entered for this course returned no student enrollments (please
									ensure that the class is in the correct term).
								</p>
								<p>Please select your class from the list below to proceed with grading.</p>
							</>
						)}

						<div className="flex gap-2">
							<ListSelect
								className="deptSelect"
								list={departmentsList}
								label="Department"
								width="500px"
								size="small"
								marginBottom="0px"
								value={selectedDept || ""}
								onChange={handleDepartmentChange}
							/>
						</div>
						{classNumList.length > 0 ? (
							<div className="w-full h-[65dvh]">
								<DataGrid
									sx={{
										"&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
											outline: "None !important",
										},
										"& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
											width: "0.4em",
										},
										"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
											background: "#f1f1f1",
										},
										"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
											backgroundColor: "#888",
										},
										"& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb:hover": {
											background: "#555",
										},

										borderRadius: "0px 4px 4px 0px",
									}}
									className="requisitions-table"
									autoPageSize
									disableSelectionOnClick
									onRowClick={(params) => {
										const id = params.row.key;
										setSelectedClassIds((prev) =>
											prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id],
										);
									}}
									showToolbar
									initialState={{
										sorting: {
											sortModel: [{ field: "std_name_matched", sort: "asc" }],
										},
									}}
									slots={{
										// footer: CustomFooter,
										toolbar: CustomToolbar,
										loadingOverlay: LinearProgress,
									}}
									rows={classNumList}
									density="standard"
									columns={columns}
									getRowId={(row) => row.key}
								/>
							</div>
						) : (
							<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
								No courses found for that department.
							</Typography>
						)}
					</div>
				</DialogContent>
			</div>
			<DialogActions>
				{isManualMode && (
					<Button onClick={handleClose} disabled={loadingUpdateClassID}>
						Cancel
					</Button>
				)}
				<Button
					color="primary"
					variant="contained"
					onClick={handleUpdateReqClassID}
					disabled={selectedClassIds.length === 0 || loadingUpdateClassID}
				>
					{loadingUpdateClassID ? (
						<CircularProgress size={24} color="inherit" />
					) : (
						"Update Grading Request With Selected Class"
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
